/**
 * Aspect — ObjectPool
 * --------------------------------------------------------------
 * Furniture instances are pooled, never created/destroyed on the fly (engine
 * rule). acquire() reuses a disabled instance or builds a new one via the
 * registry; release() disables and returns it to the free list. Teardown is the
 * only path that actually destroys entities.
 *
 * release() is idempotent: double-free must NOT push the same entity onto the
 * free list twice, or two placements will share one instance (select/move as 1).
 */

import * as pc from "playcanvas";

export class ObjectPool {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {import('./asset_registry.js').AssetRegistry} registry
   */
  constructor(app, registry) {
    this.app = app;
    this.registry = registry;
    this.root = new pc.Entity("furniture-root");
    app.root.addChild(this.root);

    /** @type {Map<string, import('playcanvas').Entity[]>} asset_id -> free list */
    this.free = new Map();
    /** @type {Set<import('playcanvas').Entity>} currently-active instances */
    this.active = new Set();
  }

  /** Pop a free entity, skipping any stale duplicates still marked active. */
  _popFree(asset_id) {
    const list = this.free.get(asset_id);
    if (!list?.length) return null;
    while (list.length) {
      const entity = list.pop();
      if (!entity) continue;
      // Skip entries that are still active (corrupt double-free residue).
      if (this.active.has(entity)) continue;
      return entity;
    }
    return null;
  }

  /** Acquire an enabled instance for an asset (reused if one is free). */
  async acquire(asset_id, glbUrl, footprint, category) {
    let entity = this._popFree(asset_id);
    // Drop pooled instances built from a different GLB URL (cache-bust / re-export).
    if (entity && entity._aspectGlbUrl && glbUrl && entity._aspectGlbUrl !== glbUrl) {
      entity.destroy?.();
      entity = null;
    }
    if (!entity) {
      entity = await this.registry.create(asset_id, glbUrl, footprint, category);
      entity._aspectAssetId = asset_id;
      entity._aspectGlbUrl = glbUrl ?? null;
      this.root.addChild(entity);
    }
    entity.enabled = true;
    this.registry.prepareDrawable(entity, entity._aspectContainerResource ?? null);
    this.active.add(entity);
    return entity;
  }

  /** Release an instance back to its pool (disabled, not destroyed). Idempotent. */
  release(entity) {
    if (!entity) return;
    // Already free (or never acquired): do not push again.
    if (!this.active.has(entity)) {
      entity.enabled = false;
      return;
    }
    entity.enabled = false;
    this.active.delete(entity);
    const asset_id = entity._aspectAssetId;
    if (!asset_id) return;
    if (!this.free.has(asset_id)) this.free.set(asset_id, []);
    this.free.get(asset_id).push(entity);
  }

  /** Destroy an instance instead of pooling it (Safari must free GPU memory). */
  discard(entity) {
    if (!entity) return;
    this.active.delete(entity);
    const asset_id = entity._aspectAssetId;
    const list = this.free.get(asset_id);
    if (list) {
      const i = list.indexOf(entity);
      if (i >= 0) list.splice(i, 1);
    }
    try {
      entity.destroy?.();
    } catch {
      /* ignore */
    }
  }

  /** Release every active instance (e.g. on room change / resync). */
  releaseAll() {
    for (const entity of [...this.active]) this.release(entity);
  }

  /** Diagnostics: total instances ever allocated (active + free). */
  stats() {
    let freeCount = 0;
    for (const list of this.free.values()) freeCount += list.length;
    return { active: this.active.size, free: freeCount, total: this.active.size + freeCount };
  }

  /** Hard teardown: destroy all pooled entities. */
  destroy() {
    this.releaseAll();
    this.root.destroy();
    this.free.clear();
    this.active.clear();
  }
}

export default ObjectPool;
